import { useState } from 'react';
import { ArrowLeft, Reply } from 'lucide-react';
import { decodeHtmlEntities } from '../../modules/email/utils/html-decode';

interface EmailMessage {
  id: string;
  from_addr: string;
  to_addr?: string;
  subject: string;
  body_text?: string;
  body_html?: string;
  received_at: string;
  direction: 'in' | 'out';
}

interface EmailThreadViewProps {
  messages: EmailMessage[];
  subject: string;
  onBack: () => void;
  onReply: () => void;
  loading?: boolean;
}

function cleanEmailContent(text: string): string {
  if (!text) return '';

  let cleaned = text;

  const quotePatterns = [
    /Le .+? à .+?, .+? <.+?> a écrit[\s\S]*/gi,
    /On .+?, .+? <.+?> wrote[\s\S]*/gi,
    /\d{1,2} \w+\.? \d{4} à \d{1,2}:\d{2}, .+? <.+?> a écrit[\s\S]*/gi,
    /Le ven\. \d{1,2} \w+\.? \d{4} à \d{1,2}:\d{2}, .+? <.+?> a écrit[\s\S]*/gi,
    /Le \d{1,2}\/\d{1,2}\/\d{4} à \d{1,2}:\d{2}, .+? a écrit[\s\S]*/gi,
    /_{10,}[\s\S]*/g,
    /On \w+, \w+ \d+, \d{4} at \d+:\d+ [AP]M .+? wrote[\s\S]*/gi,
  ];

  for (const pattern of quotePatterns) {
    cleaned = cleaned.replace(pattern, '');
  }

  cleaned = cleaned
    .split('\n')
    .filter(line => {
      const trimmed = line.trim();
      return !trimmed.startsWith('>') && !trimmed.startsWith('|') && trimmed !== '--';
    })
    .join('\n');

  cleaned = cleaned.replace(/\n{3,}/g, '\n\n').trim();

  return cleaned;
}

function extractSenderName(fromAddr: string): string {
  const match = fromAddr.match(/^(.+?)\s*<.+>$/);
  if (match) return match[1].trim();
  return fromAddr.split('@')[0];
}

export default function EmailThreadView({
  messages,
  subject,
  onBack,
  onReply,
  loading = false,
}: EmailThreadViewProps) {
  const reversedMessages = [...messages].reverse();
  const [expandedMessages, setExpandedMessages] = useState<Set<string>>(
    new Set([reversedMessages[0]?.id])
  );

  const toggleMessage = (messageId: string) => {
    setExpandedMessages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        <p>Aucun message dans ce thread</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white">
      <div className="border-b px-6 py-4 bg-gray-50">
        <div className="flex items-center gap-3 mb-2">
          <button
            onClick={onBack}
            className="p-2 hover:bg-gray-200 rounded-full transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <h2 className="text-lg font-semibold text-gray-900 flex-1 truncate">
            {decodeHtmlEntities(subject) || '(Sans objet)'}
          </h2>
        </div>
        <div className="text-sm text-gray-600 ml-11">
          {messages.length} message{messages.length > 1 ? 's' : ''}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
        {reversedMessages.map((msg, idx) => {
          const isExpanded = expandedMessages.has(msg.id);
          const isFirst = idx === 0;
          const senderName = extractSenderName(msg.from_addr);
          const rawContent = cleanEmailContent(msg.body_text || msg.body_html || '');
          const content = decodeHtmlEntities(rawContent);

          return (
            <div
              key={msg.id}
              className={`border rounded-lg transition-all ${
                msg.direction === 'in' ? 'border-blue-200 bg-blue-50/30' : 'border-green-200 bg-green-50/30'
              }`}
            >
              <div
                onClick={() => !isFirst && toggleMessage(msg.id)}
                className={`p-4 ${!isFirst ? 'cursor-pointer hover:bg-gray-50/50' : ''}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold">
                      {senderName.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">{decodeHtmlEntities(senderName)}</div>
                      <div className="text-xs text-gray-500">{decodeHtmlEntities(msg.from_addr)}</div>
                    </div>
                  </div>
                  <div className="text-xs text-gray-500">
                    {new Date(msg.received_at).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </div>
                </div>

                {(isExpanded || isFirst) && (
                  <div className="mt-3 text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                    {content || msg.body_text || '(Pas de contenu)'}
                  </div>
                )}

                {!isExpanded && !isFirst && (
                  <div className="mt-2 text-sm text-gray-500 truncate">
                    {content.split('\n')[0] || msg.body_text?.split('\n')[0] || '...'}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="border-t px-6 py-4 bg-gray-50">
        <button
          onClick={onReply}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Reply className="h-4 w-4" />
          Répondre
        </button>
      </div>
    </div>
  );
}
